import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Modal,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axiosInstance from "../axios";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  countInactiveGenieTags,
  isGenieTagActive,
  markGenieTagsActive,
} from "../../utils/genieTags";

const OrdersContent = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedPlates, setSelectedPlates] = useState([]);
  const [platesLoading, setPlatesLoading] = useState(false);
  const [platesError, setPlatesError] = useState(null);
  const [platesActionError, setPlatesActionError] = useState("");
  const [platesSuccess, setPlatesSuccess] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [activatingPlateCode, setActivatingPlateCode] = useState(null);
  const {t}=useTranslation();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axiosInstance.post("user.php?method=get_orders");
        setOrders(response.data.orders);
      } catch (err) {
        setError("Failed to fetch orders. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // Fetch plates when opening modal
  const handleOpenModal = async (orderId) => {
    setOpenModal(true);
    setPlatesLoading(true);
    setPlatesError(null);
    setPlatesActionError("");
    setPlatesSuccess("");
    setSelectedOrderId(orderId);

    try {
      const response = await axiosInstance.post(
        "user.php?method=get_order_plates",
        {
          order_id: orderId,
        }
      );
      setSelectedPlates(response.data.plates || []);
    } catch (error) {
      setPlatesError("Failed to load plates.");
    } finally {
      setPlatesLoading(false);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedPlates([]);
    setSelectedOrderId(null);
    setActivatingPlateCode(null);
    setPlatesActionError("");
    setPlatesSuccess("");
  };

  const handleActivatePlates = async (plateCode = null) => {
    if (!selectedOrderId || activatingPlateCode) return;

    const activationKey = plateCode || "__all__";
    setActivatingPlateCode(activationKey);
    setPlatesActionError("");
    setPlatesSuccess("");

    try {
      const payload = { order_id: selectedOrderId };
      if (plateCode) payload.plate_code = plateCode;

      const response = await axiosInstance.post(
        "user.php?method=activate_order_plates",
        payload
      );
      if (response.data?.status !== "success") {
        throw new Error(
          response.data?.message || t("Failed to activate Genie Tags.")
        );
      }

      setSelectedPlates((plates) => markGenieTagsActive(plates, plateCode));
      setPlatesSuccess(
        t(plateCode ? "Genie Tag activated." : "Genie Tags activated.")
      );
    } catch (error) {
      setPlatesActionError(
        error.response?.data?.message ||
          error.message ||
          t("Failed to activate Genie Tags.")
      );
    } finally {
      setActivatingPlateCode(null);
    }
  };

  const inactivePlateCount = countInactiveGenieTags(selectedPlates);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ pb: 2 }}
      >
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: "24px", sm: "28px" },
            fontWeight: 600,fontFamily:"Roboto"
          }}
        >
          {t("Your Orders")}
        </Typography>
        {/* <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/manualorder")}
          sx={{
            backgroundColor: "#2B56F5",
            "&:hover": {
              backgroundColor: "#1E3FA3",
            },
          }}
        >
          <AddIcon />
          Place Order
        </Button> */}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center"  height= "70vh" p={3}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : orders?.length === 0 ? (
        <Alert severity="info">{t("No orders found")}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: "600",fontFamily:"Roboto",letterSpacing:"0.4px" }}>{t("Order ID")}</TableCell>
                <TableCell sx={{ fontWeight: "600",fontFamily:"Roboto",letterSpacing:"0.4px" }}>{t("Date")}</TableCell>
                <TableCell sx={{ fontWeight: "600",fontFamily:"Roboto",letterSpacing:"0.4px" }}>{t("Product Name")}</TableCell>
                <TableCell sx={{ fontWeight: "600",fontFamily:"Roboto",letterSpacing:"0.4px" }}>
                 {t("Transaction ID")}
                </TableCell>
                <TableCell sx={{ fontWeight: "600",fontFamily:"Roboto",letterSpacing:"0.4px" }}>{t("Amount")}</TableCell>
                <TableCell sx={{ fontWeight: "600",fontFamily:"Roboto",letterSpacing:"0.4px" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders?.map((order) => (
                <TableRow
                  key={order.id}
                  sx={{ "&:hover": { backgroundColor: "#d8dadb" } }}
                >
                  <TableCell>{order.id}</TableCell>
                  <TableCell>
                    {new Date(order.created_at)
                      .toLocaleString("en-US", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                      .replace(",", "")}
                  </TableCell>
                  <TableCell>{order.name}</TableCell>
                  <TableCell>{order.transaction_id}</TableCell>
                  <TableCell>${order.amount}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleOpenModal(order.id)}
                      sx={{
                        backgroundColor: "#2B56F5",
                        "&:hover": {
                          backgroundColor: "#1E3FA3",
                        },textTransform:"capitalize",fontFamily:"Roboto",fontSize:"14px"
                      }}
                    >
                     {t("View Genie Tags")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal for Viewing Plates */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="plates-modal"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            maxWidth: 500,
            bgcolor: "var(--pg-card)",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            maxHeight: "85vh",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">{t("Your Genie Tags")}</Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>

          {platesLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress />
            </Box>
          ) : platesError ? (
            <Alert severity="error">{platesError}</Alert>
          ) : selectedPlates?.length > 0 ? (
            <>
              {platesActionError ? (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {platesActionError}
                </Alert>
              ) : null}
              {platesSuccess ? (
                <Alert severity="success" sx={{ mb: 1 }}>
                  {platesSuccess}
                </Alert>
              ) : null}
              <Alert
                severity={inactivePlateCount > 0 ? "warning" : "success"}
                sx={{ mb: 1 }}
              >
                {t(
                  inactivePlateCount > 0
                    ? "Inactive Genie Tags can be activated below."
                    : "All Genie Tags are active."
                )}
              </Alert>
              <TableContainer component={Paper} elevation={2}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>{t("Business")}</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                       {t("Genie Tag")}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>{t("Status")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedPlates.map((plate) => {
                      const isActive = isGenieTagActive(plate);
                      const isActivating = activatingPlateCode === plate.plate_code;

                      return (
                        <TableRow key={plate.plate_code}>
                          <TableCell>{plate.name}</TableCell>
                          <TableCell sx={{ color: "green", fontWeight: "bold" }}>
                            {plate.plate_code}
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  color: isActive ? "green" : "red",
                                  fontWeight: "bold",
                                }}
                              >
                                {t(isActive ? "Active" : "Inactive")}
                              </Typography>
                              {!isActive ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  disabled={Boolean(activatingPlateCode)}
                                  onClick={() => handleActivatePlates(plate.plate_code)}
                                  aria-label={`${t("Activate")} ${plate.plate_code}`}
                                  sx={{ textTransform: "none", minWidth: 76 }}
                                >
                                  {isActivating ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    t("Activate")
                                  )}
                                </Button>
                              ) : null}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button
                variant={inactivePlateCount > 0 ? "contained" : "outlined"}
                fullWidth
                disabled={inactivePlateCount === 0 || Boolean(activatingPlateCode)}
                onClick={() => handleActivatePlates()}
                aria-label={t("Activate all inactive tags")}
                sx={{ mt: 2, textTransform: "none" }}
              >
                {activatingPlateCode === "__all__" ? (
                  <CircularProgress size={18} color="inherit" />
                ) : inactivePlateCount > 0 ? (
                  t("Activate all inactive tags")
                ) : (
                  t("All Genie Tags are active.")
                )}
              </Button>
            </>
          ) : (
            <Typography>{t("No Genie Tags available.")}</Typography>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default OrdersContent;
